import type {
  AudioQualityAnalytics,
  AudioQualityAnalyticsResponse,
  AudioQualityStatsResponse,
} from './audioQuality.types';
import type {
  BreakdownItemResponse,
  BreakdownRow,
  ComparisonDetail,
  ComparisonDetailResponse,
  ComparisonListItem,
  ComparisonListItemResponse,
  ComparisonListResponse,
  ComparisonMetrics,
  ComparisonMetricsResponse,
  ComparisonPage,
  Regeneration,
  RegenerationResponse,
  SummaryMetrics,
  SummaryMetricsResponse,
  TimelineItemResponse,
  TimelinePoint,
  VerificationItemResponse,
  VerificationRow,
} from './types';

const mapAudioQualityStats = (response: AudioQualityStatsResponse) => ({
  totalRecordings: response.total_recordings,
  partialRecordings: response.partial_recordings,
  assessedRecordings: response.assessed_recordings,
  unknownRecordings: response.unknown_recordings,
  scoreMean: response.score_mean,
  poorRecordings: response.poor_recordings,
  poorRate: response.poor_rate,
  analysisCoverage: response.analysis_coverage,
  suppressed: response.suppressed,
});

export const mapAudioQualityAnalytics = (response: AudioQualityAnalyticsResponse): AudioQualityAnalytics => ({
  ...mapAudioQualityStats(response),
  scorerVersion: response.scorer_version,
  scorerVersions: response.scorer_versions,
  orgOptions: response.org_options,
  stateCounts: {
    assessed: response.state_counts.assessed,
    insufficientSpeech: response.state_counts.insufficient_speech,
    unavailable: response.state_counts.unavailable,
  },
  timeline: response.timeline.map((point) => ({ ...mapAudioQualityStats(point), date: point.date })),
});

const toOptionalString = (value: string | number | null) => (value == null ? null : String(value));

export const mapMetrics = (response: SummaryMetricsResponse): SummaryMetrics => ({
  generations: response.generations,
  metricRows: response.metric_rows,
  pairedSaves: response.paired_saves,
  unpairedSaves: response.unpaired_saves,
  unsaved: response.unsaved,
  editRateMean: response.edit_rate_mean,
  editRateMedian: response.edit_rate_median,
  charSimilarityMean: response.char_similarity_mean,
  editDistribution: response.edit_distribution
    ? {
        noEdits: response.edit_distribution.no_edits,
        minimal: response.edit_distribution.minimal,
        moderate: response.edit_distribution.moderate,
        major: response.edit_distribution.major,
      }
    : null,
  fallbackRate: response.fallback_rate,
  latencyP50Ms: response.e2e_latency_p50_ms,
  latencyP95Ms: response.e2e_latency_p95_ms,
  regenRate: response.regen_rate,
  suppressed: response.suppressed,
});

export const mapTimelineItem = (response: TimelineItemResponse): TimelinePoint => ({
  date: response.date,
  generations: response.generations,
  metricRows: response.metric_rows,
  editRateMean: response.edit_rate_mean,
  latencyP50Ms: response.e2e_latency_p50_ms,
  latencyP95Ms: response.e2e_latency_p95_ms,
  fallbackRate: response.fallback_rate,
  regenRate: response.regen_rate,
  suppressed: response.suppressed,
});

export const mapBreakdownItem = (response: BreakdownItemResponse): BreakdownRow => ({
  key: toOptionalString(response.key),
  label: toOptionalString(response.label),
  generations: response.generations,
  metricRows: response.metric_rows,
  editRateMean: response.edit_rate_mean,
  editRateMedian: response.edit_rate_median,
  latencyP95Ms: response.e2e_latency_p95_ms,
  fallbackRate: response.fallback_rate,
  regenRate: response.regen_rate,
  suppressed: response.suppressed,
});

export const mapRegeneration = (response: RegenerationResponse): Regeneration => ({
  generations: response.generations,
  audios: response.audios,
  regenRate: response.regen_rate,
  avgGenerationsPerAudio: response.avg_generations_per_audio,
  byTrigger: { ...response.by_trigger },
});

export const mapVerificationItem = (response: VerificationItemResponse): VerificationRow => ({
  key: response.key,
  offered: response.offered,
  accepted: response.accepted,
  rejected: response.rejected,
  unanswered: response.unanswered,
  acceptRate: response.accept_rate,
  appliedRate: response.applied_rate,
  resolveP50Seconds: response.resolve_p50_s,
  suppressed: response.suppressed,
});

const mapComparisonMetrics = (response: ComparisonMetricsResponse): ComparisonMetrics => ({
  wordDiffCount: response.word_diff_count,
  similarityPercentage: response.similarity_percentage,
  editDistance: response.edit_distance,
  changeMagnitude: response.change_magnitude,
});

const mapComparisonListItem = (response: ComparisonListItemResponse): ComparisonListItem => ({
  audioId: response.audio.id,
  createdAt: response.audio.created_at,
  metrics: response.comparison_metrics ? mapComparisonMetrics(response.comparison_metrics) : null,
});

export const mapComparisonPage = (response: ComparisonListResponse): ComparisonPage => ({
  items: response.data.map(mapComparisonListItem),
  page: response.pagination.page,
  totalPages: Math.max(1, response.pagination.total_pages),
  totalCount: response.pagination.total_count,
});

export const mapComparisonDetail = (response: ComparisonDetailResponse): ComparisonDetail => {
  const analysis = response.comparison_analysis;
  return {
    metrics: mapComparisonMetrics(analysis.metrics),
    modelText: response.summaries.model_summary.text,
    userText: response.summaries.user_summary.text,
    modelWordCount: analysis.text_analysis.model_word_count,
    userWordCount: analysis.text_analysis.user_word_count,
    modelCharacterCount: analysis.text_analysis.model_character_count,
    userCharacterCount: analysis.text_analysis.user_character_count,
    termsAdded: analysis.medical_terms.terms_added,
    termsRemoved: analysis.medical_terms.terms_removed,
    inlineDiffHtml: analysis.inline_diff_html,
  };
};
